import {
  eventBus,
  useEventBusPublisher,
  useEventBusPublisherMemoized,
  useEventBusSubscriber,
  useEventBusSubscriberMemoized,
} from '@lessonup/client-integration';

type AudioEvents = 'currentTime' | 'duration' | 'isPlaying' | 'requestPlay' | 'setCurrentTime' | 'setVolume';

const audioEventNameBuilder = (pinComponentId: string, eventName: AudioEvents) => {
  return {
    streamName: `${pinComponentId}-${eventName}-stream`,
    subscriptionName: `${pinComponentId}-${eventName}-subscription`,
  };
};

// memoized publisher and subscriber for currentTime
export const useAudioCurrentTimeEventBusPublisher = (pinComponentId: string, initialState: number) => {
  const { streamName, subscriptionName } = audioEventNameBuilder(pinComponentId, 'currentTime');
  return useEventBusPublisherMemoized<number>(eventBus, streamName, subscriptionName, initialState);
};
export const useAudioCurrentTimeEventBusSubscriber = (pinComponentId: string, handler: (payload: number) => void) => {
  const { streamName, subscriptionName } = audioEventNameBuilder(pinComponentId, 'currentTime');
  return useEventBusSubscriberMemoized<number>(eventBus, streamName, subscriptionName, handler);
};

// memoized publisher and subscriber for duration
export const useAudioDurationEventBusPublisher = (pinComponentId: string, initialState: number) => {
  const { streamName, subscriptionName } = audioEventNameBuilder(pinComponentId, 'duration');
  return useEventBusPublisherMemoized<number>(eventBus, streamName, subscriptionName, initialState);
};
export const useAudioDurationEventBusSubscriber = (pinComponentId: string, handler: (payload: number) => void) => {
  const { streamName, subscriptionName } = audioEventNameBuilder(pinComponentId, 'duration');
  return useEventBusSubscriberMemoized<number>(eventBus, streamName, subscriptionName, handler);
};

// memoized publisher and subscriber for isPlaying
export const useAudioIsPlayingEventBusPublisher = (pinComponentId: string, initialState: boolean) => {
  const { streamName, subscriptionName } = audioEventNameBuilder(pinComponentId, 'isPlaying');
  return useEventBusPublisherMemoized<boolean>(eventBus, streamName, subscriptionName, initialState);
};
export const useAudioIsPlayingEventBusSubscriber = (pinComponentId: string, handler: (payload: boolean) => void) => {
  const { streamName, subscriptionName } = audioEventNameBuilder(pinComponentId, 'isPlaying');
  return useEventBusSubscriberMemoized<boolean>(eventBus, streamName, subscriptionName, handler);
};

// publisher and subscriber for requestPlay
export const useAudioRequestPlayEventBusPublisher = (pinComponentId: string) => {
  const { streamName } = audioEventNameBuilder(pinComponentId, 'requestPlay');
  return useEventBusPublisher<boolean>(eventBus, streamName);
};
export const useAudioRequestPlayEventBusSubscriber = (pinComponentId: string, handler: (payload: boolean) => void) => {
  const { streamName } = audioEventNameBuilder(pinComponentId, 'requestPlay');
  return useEventBusSubscriber<boolean>(eventBus, streamName, handler);
};

// publisher and subscriber for setting a new currentTime
export const useAudioSetCurrentTimeEventBusPublisher = (pinComponentId: string) => {
  const { streamName } = audioEventNameBuilder(pinComponentId, 'setCurrentTime');
  return useEventBusPublisher<number>(eventBus, streamName);
};
export const useAudioSetCurrentTimeEventBusSubscriber = (
  pinComponentId: string,
  handler: (payload: number) => void
) => {
  const { streamName } = audioEventNameBuilder(pinComponentId, 'setCurrentTime');
  return useEventBusSubscriber<number>(eventBus, streamName, handler);
};

// publisher and subscriber for setting a new volume
export const useAudioSetVolumeEventBusPublisher = (pinComponentId: string) => {
  const { streamName } = audioEventNameBuilder(pinComponentId, 'setVolume');
  return useEventBusPublisher<number>(eventBus, streamName);
};
export const useAudioSetVolumeEventBusSubscriber = (pinComponentId: string, handler: (payload: number) => void) => {
  const { streamName } = audioEventNameBuilder(pinComponentId, 'setVolume');
  return useEventBusSubscriber<number>(eventBus, streamName, handler);
};
